/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useEffect } from 'react';
import { VStack } from 'chakra-v3';
import type { NgGenericTask } from '@karya/core';
import { useGenericTaskExecutor } from '@karya/generic-task-react';
import { TaskComponentItem } from './TaskComponentItem';
import ErrorDisplay from '../../../components/Error';

export interface GenericTaskRendererProps {
  controller: ReturnType<typeof useGenericTaskExecutor>;
  spec: NgGenericTask;
  isActiveAssignment?: boolean;
  scrollToBottom?: () => void;
}

/**
 * Main Task Renderer component that renders a task specification using the GenericTaskExecutor controller
 */
export const GenericTaskRenderer: React.FC<GenericTaskRendererProps> = ({
  controller,
  scrollToBottom,
  isActiveAssignment = false,
}) => {
  // Auto-scroll to bottom when new components are rendered
  useEffect(() => {
    scrollToBottom?.();
  }, [controller.currentIndex, scrollToBottom]);

  // Display parse errors to the user, except for the expected END component null reference
  if (controller.parseError) {
    return (
      <ErrorDisplay
        message="An error occurred while loading the task"
        debugMessage={`${controller.parseError?.code}: ${controller.parseError?.prettyPrint()}`}
        navigateButton={{ to: '/dashboard', label: 'Go to Dashboard' }}
      />
    );
  }

  return (
    <VStack gap="0" align="stretch">
      {controller.history.map((node, index) => {
        return (
          <TaskComponentItem
            node={node}
            index={index}
            controller={controller}
            isActiveAssignment={isActiveAssignment}
            key={`${node.component.key}-${index}`}
          />
        );
      })}
    </VStack>
  );
};
